import { fetcher, useApplicantAPI } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  ButtonGroup,
  GhostButton,
  Icon,
  LinkButton,
  PrimaryButton,
  SectionMessage,
  Space,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { RenderError } from "@certn/forms/src/components/helpers/RenderError";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { hasLabel } from "@certn/forms/src/utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApplication } from "../ApplicationContext";
import { useApplicationFormContext } from "../ApplicationFormContext";
import Constants from "../constants";
import { FlinksIFrameModal } from "../controls/income-verification/FlinksIFrameModal";
import { IncomeVerificationBaseModal } from "../controls/income-verification/IncomeVerificationBaseModal";
import { IncomeVerificationDemoSkipModal } from "../controls/income-verification/IncomeVerificationDemoSkipModal";
import { IncomeVerificationSummary } from "../controls/income-verification/IncomeVerificationSummary";
import { IncomeVerificationTextBoxesAndSkip } from "../controls/income-verification/IncomeVerificationTextBoxesAndSkip";
import { SuccessPage } from "../controls/income-verification/SuccessPage";
import { useLearnMoreModalContents } from "../controls/income-verification/constants";
import { useHandleChange } from "../utils";

function IncomeVerificationLayout(props: CascadiaLayoutProps) {
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const { t } = useTranslation();

  const { navigateToSection, prevSection } = useApplication();
  const learnMoreModalContents = useLearnMoreModalContents();
  const { caseId } = useApplicationFormContext();
  const { data: applicantData } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/` as "/api/internal/applicant/case/{id}/"
  );
  const handleChange = useHandleChange();
  const [
    flinksRedirectEventListenerSuccess,
    setFlinksRedirectEventListenerSuccess,
  ] = useState(false);
  const [showDemoSkipModal, setShowDemoSkipModal] = useState(false);

  const { uischema, schema } = props;
  const options = uischema.options || {};
  const userOtherSkipReasoning =
    props.data?.income_verification_skip?.skip_other_reasoning;

  if (isSummary) {
    return (
      <IncomeVerificationSummary otherSkipReason={userOtherSkipReasoning} />
    );
  }

  if (!hasLabel(uischema)) {
    return <RenderError>Invalid Category</RenderError>;
  }

  const setFlinksCompleted = async () => {
    try {
      await fetcher.request(
        `/api/internal/applicant/case/${caseId}/income-verification/set-flinks-completed/`,
        {
          method: "POST",
          body: JSON.stringify({
            case_id: caseId,
          }),
        }
      );
    } catch (e) {
      console.error("Error sending request:", e);
    }
  };

  const applicantFormDataOrRedirectEventIsFlinksCompleted =
    applicantData?.applicant_form_data?.flinks_completed ||
    flinksRedirectEventListenerSuccess;
  const incomeVerificationSkipped =
    props.data?.income_verification_skip?.skip_verification;
  const canSubmit =
    incomeVerificationSkipped === true ||
    applicantFormDataOrRedirectEventIsFlinksCompleted;
  const userAdditionalInformation =
    props.data?.income_verification_additional_information;
  window.addEventListener("message", (e) => {
    if (e.data.step === "REDIRECT") {
      // https://docs.flinks.com/docs/events
      setFlinksRedirectEventListenerSuccess(true);
      setFlinksCompleted();
    }
  });

  const skipOptions =
    schema?.properties?.income_verification_skip?.properties?.skip_reason.oneOf;

  return (
    <BaseLayout {...props} className={styles.layout}>
      {applicantFormDataOrRedirectEventIsFlinksCompleted ? (
        <SuccessPage
          openTextArea={Boolean(userAdditionalInformation)}
          additionalInformation={userAdditionalInformation}
        />
      ) : (
        <>
          <div className={styles.heading}>
            <Text
              as="h1"
              style="form/page-title"
              testID="identity-verification-section-title"
            >
              {uischema.label}
            </Text>
            {options.subLabel && (
              <Text
                style="form/body-primary"
                color="text-weak"
                testID="identity-verification-section-subtitle"
              >
                {options.subLabel}
              </Text>
            )}
          </div>
          <div className={styles.sectionMessagePadding}>
            <SectionMessage
              sectionTitle={t("Your privacy is important to us")}
              type="success"
              icon="verified_user"
            >
              {t(
                "Certn has partnered with Flinks to securely verify income directly with your bank. Information we collect and share in the report includes basic information about you and your income sources."
              )}{" "}
              <IncomeVerificationBaseModal
                header={t("Information we may collect")}
                link={t("Learn more")}
                content={learnMoreModalContents}
              />
            </SectionMessage>
          </div>
          <Stack gap="space-4">
            <IncomeVerificationTextBoxesAndSkip
              skipOptions={skipOptions}
              otherSkipReason={userOtherSkipReasoning}
            />
            <FlinksIFrameModal
              schema={schema}
              incomeVerificationSkipped={incomeVerificationSkipped}
            />
            {incomeVerificationSkipped && (
              <div>
                <Text color="text-warning" style="form/body-secondary">
                  <Icon
                    className={styles.iconStyles}
                    fill
                    name="warning"
                    color="text-warning"
                  />
                  {t("You have chosen to skip this step.")}
                </Text>{" "}
                <LinkButton
                  textStyle="form/body-secondary"
                  textColor="text-warning"
                  testID="income-verification-undo-skip-button"
                  onPress={() => {
                    handleChange("income_verification_skip", {
                      skip_verification: false,
                      skip_reason: undefined,
                      skip_other_reasoning: undefined,
                    });
                    showToast(t("Skip information has been removed."), {
                      type: "success",
                    });
                  }}
                >
                  {t("Undo")}
                </LinkButton>
              </div>
            )}
          </Stack>
        </>
      )}
      <div className={styles.actionBar}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              testID="section-back-button"
              leftIcon="chevron_left"
              onPress={async () => {
                navigateToSection(prevSection);
              }}
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          {Constants.enableDemoResponses &&
            !applicantFormDataOrRedirectEventIsFlinksCompleted && (
              <BasicButton onPress={() => setShowDemoSkipModal(true)}>
                {t("Demo Response")}
              </BasicButton>
            )}
          {props.data && (
            <PrimaryButton
              testID="section-next-step-button"
              type="submit"
              isDisabled={!canSubmit}
            >
              {t("Next step")}
            </PrimaryButton>
          )}
        </ButtonGroup>
      </div>
      {Constants.enableDemoResponses && (
        <IncomeVerificationDemoSkipModal
          isOpen={showDemoSkipModal}
          onClose={() => {
            handleChange("income_verification_country", "CA");
            setShowDemoSkipModal(false);
          }}
          onSave={() => {
            handleChange("income_verification_country", "CA");
            setShowDemoSkipModal(false);
          }}
        />
      )}
    </BaseLayout>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  illustration: css({
    display: "flex",
    justifyContent: "center",
    paddingTop: "var(--space-12)",
    paddingBottom: "var(--space-12)",
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
  sectionMessagePadding: css({
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
  paddingBottom: css({
    paddingBottom: "var(--space-2)",
  }),
  iconStyles: css({
    verticalAlign: "bottom",
    paddingRight: "4px",
  }),
};

IncomeVerificationLayout.displayName = "IncomeVerificationLayout";

export const IncomeVerificationLayoutRenderer = withCascadiaLayoutProps(
  IncomeVerificationLayout
);
